import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useAutoSave — handles document autosave (20 s debounce), page-hide save,
 * Cmd/Ctrl+S shortcut, and the resulting notification state.
 *
 * pageSettingsRef: always-current ref to page settings — included in every save.
 */
export function useAutoSave({ editor, socketRef, contractId, t, lastSavedContent, pageSettingsRef }) {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const autosaveTimeoutRef = useRef(null);
  // Keep hasUnsavedChanges readable inside callbacks without stale closure
  const hasUnsavedRef = useRef(false);
  hasUnsavedRef.current = hasUnsavedChanges;

  const closeNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  // Build the settings payload — always reads from the ref (never stale)
  const buildSavePayload = useCallback((content) => {
    const payload = { content };
    if (pageSettingsRef?.current) {
      payload.settings = { page: pageSettingsRef.current };
    }
    return payload;
  }, [pageSettingsRef]);

  const saveContract = useCallback(() => {
    if (socketRef.current && editor && hasUnsavedRef.current) {
      setNotification({
        open: true,
        message: t('document_editor.saving_document'),
        severity: 'info',
      });

      const content = editor.getHTML();
      socketRef.current.emit('save', buildSavePayload(content));
      if (lastSavedContent) lastSavedContent.current = content;
      setHasUnsavedChanges(false);

      setTimeout(() => {
        setNotification({
          open: true,
          message: t('document_editor.document_saved'),
          severity: 'success',
        });
      }, 1000);
    } else if (!hasUnsavedRef.current) {
      // Nothing to save
    } else {
      setNotification({
        open: true,
        message: t('document_editor.cannot_save'),
        severity: 'error',
      });
    }
  }, [editor, socketRef, t, lastSavedContent, buildSavePayload]);

  // Detect changes and schedule autosave (20 s)
  useEffect(() => {
    if (!editor) return;

    const handleDocumentChange = () => {
      const currentContent = editor.getHTML();
      if (currentContent !== lastSavedContent?.current) {
        setHasUnsavedChanges(true);
      } else {
        setHasUnsavedChanges(false);
        return;
      }

      clearTimeout(autosaveTimeoutRef.current);
      autosaveTimeoutRef.current = setTimeout(() => {
        if (hasUnsavedRef.current) saveContract();
      }, 20000);
    };

    editor.on('update', handleDocumentChange);
    return () => {
      editor.off('update', handleDocumentChange);
      clearTimeout(autosaveTimeoutRef.current);
    };
  }, [editor, saveContract, lastSavedContent]);

  // Save when user navigates away (tab hidden)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && hasUnsavedRef.current) {
        saveContract();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [saveContract]);

  // Cancel pending autosave on unmount (the save itself is handled by
  // useCollaboration's cleanup which fires before socket.disconnect).
  useEffect(() => {
    return () => {
      clearTimeout(autosaveTimeoutRef.current);
    };
  }, []);

  // Cmd/Ctrl+S keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (socketRef.current && editor) {
          setNotification({
            open: true,
            message: t('document_editor.saving_document'),
            severity: 'info',
          });
          const content = editor.getHTML();
          socketRef.current.emit('save', buildSavePayload(content));
          if (lastSavedContent) lastSavedContent.current = content;
          setHasUnsavedChanges(false);
          setTimeout(() => {
            setNotification({
              open: true,
              message: t('document_editor.document_saved'),
              severity: 'success',
            });
          }, 1000);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [editor, socketRef, t, lastSavedContent, buildSavePayload]);

  return {
    hasUnsavedChanges,
    setHasUnsavedChanges,
    saveContract,
    notification,
    closeNotification,
  };
}
